import { CSSProperties } from "react"
import { DraggableAttributes, DraggableSyntheticListeners } from "@dnd-kit/core"
import { SyncOutlined } from "@ant-design/icons"
import { Tooltip } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCalendarCheck } from "@fortawesome/pro-regular-svg-icons"
import { faSnowflake } from "@fortawesome/pro-solid-svg-icons"
import { TaskCalendarEvent } from "../scheduleApi"

export const SPANNING_CARD_BORDER_RADIUS = 4
export const SPANNING_CARD_COLOR_STRIP_WIDTH = 8

type StyleParams = {
  color: string
  isStart: boolean
  isEnd: boolean
}

const getBorderRadius = (isStart: boolean, isEnd: boolean): CSSProperties["borderRadius"] => {
  if (isStart && isEnd) {
    return SPANNING_CARD_BORDER_RADIUS
  }
  if (isStart) {
    return `${SPANNING_CARD_BORDER_RADIUS}px 0 0 ${SPANNING_CARD_BORDER_RADIUS}px`
  }
  if (isEnd) {
    return `0 ${SPANNING_CARD_BORDER_RADIUS}px ${SPANNING_CARD_BORDER_RADIUS}px 0`
  }
  return 0
}

export const getSpanningTaskOuterStyles = ({ color, isStart, isEnd }: StyleParams): CSSProperties => ({
  backgroundColor: isStart ? color : '#ffffff',
  border: `1px solid ${color}`,
  borderRadius: getBorderRadius(isStart, isEnd),
  overflow: "hidden",
  boxSizing: "border-box",
  ...(isStart ? {} : {
    display: "flex",
    alignItems: "stretch",
    gap: 4,
    padding: "3px 6px",
  }),
})

export const getSpanningTaskInnerStyle = ({ isEnd }: { isEnd: boolean }): CSSProperties => ({
  position: "absolute",
  top: 0,
  bottom: 0,
  left: SPANNING_CARD_COLOR_STRIP_WIDTH,
  right: 0,
  backgroundColor: '#ffffff',
  borderRadius: isEnd ? SPANNING_CARD_BORDER_RADIUS : `${SPANNING_CARD_BORDER_RADIUS}px 0 0 ${SPANNING_CARD_BORDER_RADIUS}px`,
  display: "flex",
  alignItems: "stretch",
  gap: 4,
  padding: "3px 6px",
  overflow: "hidden",
  boxSizing: "border-box",
})

type SpanningTaskContentProps = {
  task: TaskCalendarEvent
  isStart: boolean
  isEnd: boolean
  isResizing: boolean
  isCompleted: boolean
  titleFontSize: number
}

export const SpanningTaskContent = ({
  task,
  isStart,
  isEnd,
  isResizing,
  isCompleted,
  titleFontSize,
}: SpanningTaskContentProps) => (
  <>
    {!isStart && (
      <span style={{ color: "#999", fontSize: 10, flexShrink: 0, alignSelf: 'center' }}>◀</span>
    )}
    <div style={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1, minWidth: 0, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#999', fontSize: 10, lineHeight: 1.2 }}>
        <FontAwesomeIcon icon={faCalendarCheck} style={{ fontSize: 10 }} />
        <span>TASK</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <span
          style={{
            fontWeight: 500,
            fontSize: titleFontSize,
            lineHeight: 1.2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: 1,
            minWidth: 0,
            textDecoration: isCompleted ? 'line-through' : undefined,
            color: isCompleted ? '#999' : undefined,
          }}
        >
          {task.title}
        </span>
        {task.frozen && (
          <Tooltip title="Frozen">
            <FontAwesomeIcon icon={faSnowflake} style={{ color: '#0958d9', fontSize: 11, flexShrink: 0 }} />
          </Tooltip>
        )}
        {task.repeatingId && (
          <SyncOutlined style={{ color: '#999', fontSize: '11px', flexShrink: 0 }} />
        )}
      </div>
      <span style={{ color: '#666', fontSize: 11, lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>All Day</span>
    </div>
    {!isEnd && !isResizing && (
      <span style={{ color: "#999", fontSize: 10, flexShrink: 0, alignSelf: 'center' }}>▶</span>
    )}
  </>
)

type SpanningTaskResizeHandleProps = {
  setNodeRef: (node: HTMLElement | null) => void
  listeners: DraggableSyntheticListeners
  attributes: DraggableAttributes
  color: string
}

export const SpanningTaskResizeHandle = ({
  setNodeRef,
  listeners,
  attributes,
  color,
}: SpanningTaskResizeHandleProps) => (
  <div
    ref={setNodeRef}
    {...listeners}
    {...attributes}
    style={{
      position: "absolute",
      top: 0,
      right: 0,
      bottom: 0,
      width: 8,
      cursor: "ew-resize",
      borderTopRightRadius: SPANNING_CARD_BORDER_RADIUS,
      borderBottomRightRadius: SPANNING_CARD_BORDER_RADIUS,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    }}
  >
    <div
      style={{
        width: 3,
        height: 12,
        backgroundColor: color,
        borderRadius: 2,
      }}
    />
  </div>
)
